/* ==========================================================================
   SS HERO controller. 2026-09-03. Rotates the homepage frames.

   - Timer crossfade every data-interval ms (default 7000), pause while the
     pointer is over the hero, while a control has focus, while the tab is
     hidden, and after the reader touches a dot or swipes (they took the
     wheel; the timer restarts after one idle interval).
   - Dots: click, ArrowLeft / ArrowRight, Home / End.
   - Swipe: pointer events, 40px threshold, horizontal only, no preventDefault
     on the vertical axis so the page still scrolls.
   - Reduced motion: no timer, frame 1 stays, dots still switch frames.
   - No frame is ever hidden by script failure: the first frame carries
     .is-active in the markup.
   ========================================================================== */
(function () {
  'use strict';

  function init(root) {
    if (!root || root.hasAttribute('data-ss-hero-ready')) return;
    root.setAttribute('data-ss-hero-ready', '');

    var frames = Array.prototype.slice.call(root.querySelectorAll('.ss-hero__frame'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('.ss-hero__dot'));
    var live = root.querySelector('[data-ss-hero-live]');
    if (frames.length < 2) return;

    var reduce = false;
    try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
    var interval = parseInt(root.getAttribute('data-interval'), 10) || 7000;
    var autoplay = root.getAttribute('data-autoplay') !== 'false' && !reduce;
    var idx = 0;
    var timer = null;
    var held = false;   /* pointer over, focus within, or reader driving */
    var hidden = false;

    root.style.setProperty('--ss-hero-dur', interval + 'ms');

    /* Veg mode: each frame's <picture> carries both variants as data
       attributes; swap src/srcset whenever html[data-diet-mode] changes.
       Deterministic, no overlay, no opacity gate. */
    function vegOn() {
      return document.documentElement.getAttribute('data-diet-mode') === 'veg';
    }
    function applyDiet() {
      var want = vegOn() ? 'veg' : 'nonveg';
      frames.forEach(function (f) {
        var pic = f.querySelector('[data-ss-pic]');
        if (!pic) return;
        var key = (want === 'veg' && pic.hasAttribute('data-has-veg')) ? 'veg' : 'nonveg';
        if (pic.getAttribute('data-diet-now') === key) return;
        pic.setAttribute('data-diet-now', key);
        var srcs = pic.querySelectorAll('source');
        for (var i = 0; i < srcs.length; i++) {
          var ss = srcs[i].getAttribute('data-' + key + '-srcset');
          if (ss) srcs[i].setAttribute('srcset', ss);
        }
        var img = pic.querySelector('img');
        if (!img) return;
        var set = img.getAttribute('data-' + key + '-srcset');
        var src = img.getAttribute('data-' + key + '-src');
        if (set) img.setAttribute('srcset', set);
        if (src) img.setAttribute('src', src);
      });
    }
    if ('MutationObserver' in window) {
      new MutationObserver(applyDiet).observe(document.documentElement, { attributes: true, attributeFilter: ['data-diet-mode'] });
    }
    applyDiet();

    function show(n, why) {
      n = (n + frames.length) % frames.length;
      if (n === idx && why !== 'init') return;
      var prev = idx;
      idx = n;
      frames.forEach(function (f, i) {
        var on = i === idx;
        f.classList.toggle('is-active', on);
        f.setAttribute('aria-hidden', on ? 'false' : 'true');
        /* lazy frames: promote the image the first time it is shown */
        if (on) {
          var imgs = f.querySelectorAll('img[loading="lazy"]');
          for (var k = 0; k < imgs.length; k++) imgs[k].loading = 'eager';
        }
      });
      dots.forEach(function (d, i) {
        var on = i === idx;
        d.setAttribute('aria-current', on ? 'true' : 'false');
        d.setAttribute('tabindex', on ? '0' : '-1');
      });
      /* restart the progress hairline */
      var cur = dots[idx];
      if (cur) { cur.style.animation = 'none'; void cur.offsetWidth; cur.style.animation = ''; }
      if (live && why !== 'init') live.textContent = 'Frame ' + (idx + 1) + ' of ' + frames.length;
      if (prev !== idx && why === 'reader') rest();
    }

    function tick() { show(idx + 1, 'timer'); }
    function play() {
      if (!autoplay || held || hidden) return;
      stop();
      timer = setInterval(tick, interval);
      root.classList.add('is-playing');
      root.classList.remove('is-paused');
    }
    function stop() {
      if (timer) { clearInterval(timer); timer = null; }
    }
    function pause() {
      stop();
      root.classList.add('is-paused');
    }
    /* the reader drove: hold one interval, then resume */
    var restT = null;
    function rest() {
      stop();
      root.classList.remove('is-playing');
      clearTimeout(restT);
      restT = setTimeout(function () { if (!held && !hidden) play(); }, interval);
    }

    /* dots */
    dots.forEach(function (d, i) {
      d.addEventListener('click', function () { show(i, 'reader'); });
      d.addEventListener('keydown', function (e) {
        var k = e.key;
        if (k === 'ArrowRight' || k === 'ArrowDown') { e.preventDefault(); show(idx + 1, 'reader'); dots[idx].focus(); }
        else if (k === 'ArrowLeft' || k === 'ArrowUp') { e.preventDefault(); show(idx - 1, 'reader'); dots[idx].focus(); }
        else if (k === 'Home') { e.preventDefault(); show(0, 'reader'); dots[idx].focus(); }
        else if (k === 'End') { e.preventDefault(); show(frames.length - 1, 'reader'); dots[idx].focus(); }
      });
    });

    /* hover and focus hold the frame */
    root.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') { held = true; pause(); } });
    root.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') { held = false; play(); } });
    root.addEventListener('focusin', function () { held = true; pause(); });
    root.addEventListener('focusout', function (e) {
      if (!root.contains(e.relatedTarget)) { held = false; play(); }
    });
    document.addEventListener('visibilitychange', function () {
      hidden = !!document.hidden;
      if (hidden) stop(); else play();
    });

    /* swipe */
    var sx = 0, sy = 0, tracking = false, moved = false;
    root.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse') return;
      sx = e.clientX; sy = e.clientY; tracking = true; moved = false;
    }, { passive: true });
    root.addEventListener('pointermove', function (e) {
      if (!tracking || moved) return;
      var dx = e.clientX - sx, dy = e.clientY - sy;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) {
        moved = true;
        show(dx < 0 ? idx + 1 : idx - 1, 'reader');
      }
    }, { passive: true });
    root.addEventListener('pointerup', function () { tracking = false; }, { passive: true });
    root.addEventListener('pointercancel', function () { tracking = false; }, { passive: true });

    show(0, 'init');
    play();
  }

  function boot() {
    var roots = document.querySelectorAll('[data-ss-hero]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', function () { setTimeout(boot, 30); });
})();
